import { ApexBarChart } from '@/components'
import { GraphDataState } from '@/redux/slices/golfSearchSlice'
import styled from 'styled-components'
import { useEffect, useState } from 'react'

interface RegionGraphProps {
  regionAvgPrice: GraphDataState['regionAvgPrice']
}

export const RegionGraph = ({ regionAvgPrice }: RegionGraphProps) => {
  const [isMobile, setIsMobile] = useState(false)
  const categories = regionAvgPrice.map((region) => region.areaNm)

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)

    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const ApexBarData = {
    options: {
      chart: {
        type: 'bar',
        height: 350,
        toolbar: { show: false },
        stacked: false,
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '55%',
          endingShape: 'rounded',
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        show: true,
        width: 2,
        curve: 'smooth',
      },
      markers: {
        size: 5,
        strokeWidth: 2,
        hover: {
          size: 7,
        },
        shape: 'circle',
        showNullDataPoints: false,
      },
      xaxis: {
        categories: categories,
        labels: {
          style: {
            fontSize: isMobile ? '10px' : '12px',
          },
        },
      },
      yaxis: {
        title: {
          text: '단위 (원)',
          style: {
            fontSize: isMobile ? '12px' : '14px',
          },
        },
        labels: {
          formatter: (value: any) => value.toLocaleString('ko-KR'),
          style: {
            fontSize: isMobile ? '10px' : '12px',
          },
        },
      },
      legend: {
        fontSize: isMobile ? '12px' : '14px',
      },
    },
    series: [
      {
        name: '비회원(주중)',
        type: 'bar',
        data: regionAvgPrice.map((region) => region.n01 ?? 0),
      },
      {
        name: '비회원(주말)',
        type: 'bar',
        data: regionAvgPrice.map((region) => region.n02 ?? 0),
      },
      {
        name: '회원(주중)',
        type: 'line',
        data: regionAvgPrice.map((region) => region.m01 ?? 0),
      },
      {
        name: '회원(주말)',
        type: 'line',
        data: regionAvgPrice.map((region) => region.m02 ?? 0),
      },
    ],
  }

  return (
    <>
      {regionAvgPrice.length > 0 && (
        <GraphWrapper>
          <GraphTitle>지역별 평균</GraphTitle>
          <ChartContainer isMobile={isMobile}>
            <ApexBarChart
              options={ApexBarData.options}
              series={ApexBarData.series}
              height="350px"
              width={isMobile ? '600px' : '100%'} // 모바일에서 고정 너비
            />
          </ChartContainer>
        </GraphWrapper>
      )}
    </>
  )
}

const GraphWrapper = styled.div`
  border: 1px solid #ddd;
  padding: 15px;
  border-radius: 8px;
  width: 100%;

  @media (max-width: 768px) {
    padding: 10px;
  }
`

const GraphTitle = styled.p`
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  margin-bottom: 15px;

  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 10px;
  }
`

const ChartContainer = styled.div<{ isMobile: boolean }>`
  ${({ isMobile }) =>
    isMobile
      ? `
        overflow-x: auto;
        overflow-y: hidden;
        width: 100%;
        
        &::-webkit-scrollbar {
          height: 6px;
        }
        
        &::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 3px;
        }
        
        &::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 3px;
        }
        
        &::-webkit-scrollbar-thumb:hover {
          background: #a1a1a1;
        }
      `
      : `
        width: 100%;
      `}
`
